import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { login as loginApi, register as registerApi, getMe } from '@/lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('vs_token');
    if (!token) { setLoading(false); return; }
    try {
      const { data } = await getMe();
      setUser(data);
    } catch {
      localStorage.removeItem('vs_token');
      localStorage.removeItem('vs_user');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await loginApi({ email, password });
    localStorage.setItem('vs_token', data.token);
    setUser(data.user);
    return data;
  };

  const signup = async (formData) => {
    const { data } = await registerApi(formData);
    localStorage.setItem('vs_token', data.token);
    setUser(data.user);
    return data;
  };

  const logout = () => {
    localStorage.removeItem('vs_token');
    localStorage.removeItem('vs_user');
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const { data } = await getMe();
      setUser(data);
    } catch { /* ignore */ }
  };

  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'manager';
  const isSuperAdmin = user?.role === 'superadmin';
  const isSubscriptionActive = user?.subscription_status === 'active' || isSuperAdmin;

  return (
    <AuthContext.Provider value={{ user, loading, login, signup, logout, checkAuth, refreshUser, isAdmin, isManager, isSuperAdmin, isSubscriptionActive }}>
      {children}
    </AuthContext.Provider>
  );
};
